import React, { forwardRef, useImperativeHandle, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    Slider,
    Typography,
    IconButton,
    Box,
    Chip,
    Stack,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { Add, Delete, Save, Close, Visibility, VisibilityOff } from '@mui/icons-material';

const BrowserTabsEditor = forwardRef(({ onSave }, ref) => {
    const [open, setOpen] = useState(false);
    const [tabs, setTabs] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [timeUnit, setTimeUnit] = useState('seconds');
    const [reloadTimeUnit, setReloadTimeUnit] = useState('seconds');
    const [params, setParams] = useState({})

    useImperativeHandle(ref, () => ({
        open: (p = null) => {
            if (p) {
                setParams(p)
                setTabs(p.tabs);
                setIsEditing(true);
            } else {
                setTabs([createNewTab()]);
                setIsEditing(false);
            }
            setOpen(true);
        },
        close: () => setOpen(false)
    }));

    const createNewTab = () => ({
        name: `Nova Aba ${tabs.length + 1}`,
        url: '',
        timeout: 10000,
        zoom: 100,
        active: true,
        reloadOnOpen: true,
        reloadTime: 0,
        id: Date.now().toString()
    });

    const handleAddTab = () => setTabs([...tabs, createNewTab()]);
    const handleRemoveTab = (id) => setTabs(tabs.filter(tab => tab.id !== id));

    const handleTabChange = (id, field, value) => {
        setTabs(tabs.map(tab => tab.id === id ? { ...tab, [field]: value } : tab));
    };

    const toggleTabActive = (id) => {
        setTabs(tabs.map(tab => 
        tab.id === id ? { ...tab, active: !tab.active } : tab
        ));
    };

    const formatTime = (ms, unit) => {
        switch(unit) {
        case 'seconds':
            return `${Math.round(ms/1000)} segundos`;
        case 'minutes':
            return `${Math.round(ms/60000)} minutos`;
        case 'hours':
            return `${(ms/3600000).toFixed(1)} horas`;
        default:
            return `${ms}ms`;
        }
    };

    const convertToMs = (value, unit) => {
        switch(unit) {
        case 'seconds': return value * 1000;
        case 'minutes': return value * 60000;
        case 'hours': return value * 3600000;
        default: return value;
        }
    };

    const convertFromMs = (ms, unit) => {
        switch(unit) {
        case 'seconds': return ms / 1000;
        case 'minutes': return ms / 60000;
        case 'hours': return ms / 3600000;
        default: return ms;
        }
    };

    const getSliderParams = (unit, isReload = false) => {
        if (isReload) {
        return {
            min: 0,
            max: unit === 'seconds' ? 300 : unit === 'minutes' ? 60 : 24,
            step: unit === 'seconds' ? 5 : unit === 'minutes' ? 1 : 0.5
        };
        }
        return {
        min: 1,
        max: unit === 'seconds' ? 120 : unit === 'minutes' ? 30 : 5,
        step: unit === 'seconds' ? 1 : unit === 'minutes' ? 1 : 0.1
        };
    };

    const handleSave = () => {
        onSave({...params, tabs});
        setOpen(false);
    };

    return (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            py: 1.5
        }}>
            <Typography variant="subtitle1" fontWeight="medium">
            {isEditing ? 'Editar Abas' : 'Criar Abas'}
            </Typography>
            <IconButton onClick={() => setOpen(false)} size="small">
            <Close fontSize="small" />
            </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2 }}>
            <Stack spacing={2}>
            <Button 
                variant="outlined" 
                startIcon={<Add />}
                onClick={handleAddTab}
                size="small"
                sx={{ alignSelf: 'flex-start' }}
            >
                Adicionar Aba
            </Button>

            {tabs.map((tab) => (
                <Box key={tab.id} sx={{ 
                p: 2, 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                opacity: tab.active ? 1 : 0.7
                }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5
                }}>
                    <TextField
                    value={tab.name}
                    onChange={(e) => handleTabChange(tab.id, 'name', e.target.value)}
                    variant="standard"
                    size="small"
                    sx={{ flexGrow: 1, mr: 1 }}
                    />
                    <IconButton 
                    onClick={() => toggleTabActive(tab.id)}
                    size="small"
                    color={tab.active ? 'success' : 'default'}
                    sx={{ ml: 1 }}
                    >
                    {tab.active ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                    </IconButton>
                    <IconButton 
                    onClick={() => handleRemoveTab(tab.id)}
                    size="small"
                    sx={{ ml: 1 }}
                    >
                    <Delete fontSize="small" />
                    </IconButton>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="URL"
                        value={tab.url}
                        onChange={(e) => handleTabChange(tab.id, 'url', e.target.value)}
                        size="small"
                        placeholder="https://"
                    />
                    </Grid>

                    <Grid item xs={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" display="block" gutterBottom>
                        Exibição: {formatTime(tab.timeout, timeUnit)}
                        </Typography>
                        <ToggleButtonGroup
                        value={timeUnit}
                        exclusive
                        onChange={(_, newUnit) => setTimeUnit(newUnit)}
                        size="small"
                        sx={{ mb: 1 }}
                        >
                        <ToggleButton value="seconds" size="small">s</ToggleButton>
                        <ToggleButton value="minutes" size="small">m</ToggleButton>
                        <ToggleButton value="hours" size="small">h</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Slider
                        value={convertFromMs(tab.timeout, timeUnit)}
                        onChange={(_, value) => handleTabChange(tab.id, 'timeout', convertToMs(value, timeUnit))}
                        {...getSliderParams(timeUnit)}
                        size="small"
                    />
                    </Grid>

                    <Grid item xs={6}>
                    <Typography variant="caption" display="block" gutterBottom>
                        Zoom: {tab.zoom}%
                    </Typography>
                    <Slider
                        value={tab.zoom}
                        onChange={(_, value) => handleTabChange(tab.id, 'zoom', value)}
                        min={25}
                        max={500}
                        step={5}
                        size="small"
                    />
                    </Grid>

                    <Grid item xs={12}>
                    <FormControlLabel
                        control={
                        <Switch
                            checked={tab.reloadOnOpen}
                            onChange={(e) => handleTabChange(tab.id, 'reloadOnOpen', e.target.checked)}
                            size="small"
                        />
                        }
                        label="Recarregar ao abrir"
                        sx={{ mr: 2 }}
                    />

                    {!tab.reloadOnOpen && (
                        <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" display="block" gutterBottom>
                            Recarregar: {formatTime(tab.reloadTime, reloadTimeUnit)}
                            </Typography>
                            <ToggleButtonGroup
                            value={reloadTimeUnit}
                            exclusive
                            onChange={(_, newUnit) => setReloadTimeUnit(newUnit)}
                            size="small"
                            sx={{ mb: 1 }}
                            >
                            <ToggleButton value="seconds" size="small">s</ToggleButton>
                            <ToggleButton value="minutes" size="small">m</ToggleButton>
                            <ToggleButton value="hours" size="small">h</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <Slider
                            value={convertFromMs(tab.reloadTime, reloadTimeUnit)}
                            onChange={(_, value) => handleTabChange(tab.id, 'reloadTime', convertToMs(value, reloadTimeUnit))}
                            {...getSliderParams(reloadTimeUnit, true)}
                            size="small"
                        />
                        </>
                    )}
                    </Grid>
                </Grid>
                </Box>
            ))}
            </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5 }}>
            <Button 
            onClick={() => setOpen(false)}
            size="small"
            sx={{ mr: 1 }}
            >
            Cancelar
            </Button>
            <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            size="small"
            disabled={tabs.length === 0 || tabs.some(tab => !tab.url)}
            >
            Salvar
            </Button>
        </DialogActions>
        </Dialog>
    );
});

export default BrowserTabsEditor;